<script>
	import {onDestroy, onMount} from "svelte";
	import {fade, slide} from "svelte/transition";
	import Loader from "../Common/Loader.svelte";
	import { copy, ignore, makeWebsiteRequest } from "../Common/tools";
	import {AppCache} from "../stores";
	import ManualFulfillment from "./Fulfillment/ManualFulfillment.svelte";
	import OrderItemAdd from "./OrderItemAdd.svelte";
	import OrderItemEdit from "./OrderItemEdit.svelte";
	import { External, M } from "../external";
	import PhoneInput from "../Common/PhoneInput.svelte";

  export let id = 0,
    options = {};

  export function isChanged() {
    return changed;
  }

  let order = null;
  let details = {};
  let products = null;
  let changed = false;
  let createdTime = "";
  let showingNotificationBox = false;
  let hasThirdPartyFulfillmentApp = false;
  let isFulfilled = false;
  let hasWaybill = false;
  let isFulfillmentOrderCreated = false;
  let billingDetailsVisible = false;

  const bulkEditFields = ["customerName", "customerEmail", "shippingAddress", "billingAddress", "paid", "status"];

  let hasPhysicalProducts = false;
  let hasDigitalGoods = false;

  $: {
    isFulfilled = false;
    hasWaybill = false;
    hasThirdPartyFulfillmentApp = false;
    if (order) {
      order.items.forEach(function (item) {
        if (item.type === "DIGITAL" || item.type === "MEMBERSHIP" || item.type === "PAYWALL") {
          hasDigitalGoods = true;
        } else if (item.type === "PHYSICAL") {
          hasPhysicalProducts = true;
        }
      });

      if (order.shipping && order.meta && (order.meta.courier || order.meta.trackingNo)) {
        isFulfilled = true;
      }

      hasThirdPartyFulfillmentApp = order.shipping && order.shipping.kind !== "system";
      if (hasThirdPartyFulfillmentApp) {
        switch (order.shipping.kind) {
          case "speedy":
          case "speedy-address":
          case "speedy-office":
            hasWaybill = ((order.extra || {}).speedy || {}).waybill ? true : false;
            break;
        }
      }

      isFulfillmentOrderCreated = order.extra?.fulfillmentOrder ? true : false;
    }
  }

  let getImageSrc = (item) => {
    let src = External.resources + "/images/image-avatar.png";
    if (item.images && item.images.length > 0) {
      src = External.getThumbnailsFromBase(item.images[0].base, "160x120", true) + item.images[0].name;
    }
    return src;
  };

  let getVariation = (item) => {
    let variations = "";
    if (item.variation && item.variation.length) {
      variations = item.variation.map((variation) => variation.value).join(", ");
    }

    return variations || (item.additions && item.additions.length ? "" : "-");
  };

  let getPaymentMethod = () => {
    let paymentType = order.paymentType || "-";
    switch (paymentType) {
      case "cod":
        paymentType = M.PAYMENT_COD;
        break;
      case "bank":
        paymentType = M.PAYMENT_BANK;
        break;
      case "stripe":
      case "stripeConnect":
      case "stripe-connect":
        paymentType = M.PAYMENT_STRIPE;
        break;
      case "braintree":
        paymentType = M.PAYMENT_BRAINTREE;
        break;
      case "2checkout":
        paymentType = M.PAYMENT_2CHECKOUT;
        break;
      case "paypal":
        paymentType = M.PAYMENT_PAYPAL;
        break;
      case "no-payment":
        paymentType = "-";
        break;
      default:
        paymentType = paymentType.charAt(0).toUpperCase() + paymentType.slice(1);
        break;
    }
    return paymentType;
  };

  let showProductOptions = (event, item) => {
    const items = [
      { text: M.MODIFY_ITEM, icon: "fa-pencil", tag: "edit" },
      { text: M.DELETE, icon: "fa-trash", tag: "delete" }
    ];

    External.Tools.showTooltipOptions(External.$(event.target), items, function (target, idx, text, tag) {
      if (tag === "edit") {
        External.Tools.showWindow({
          noTitle: true,
          width: 520,
          isModal: false,
          withDarkBackground: true,
          forceAtRight: External.showBuilderPopupsOnFullHeight,
          action: {
            onShow: function (popup, ui) {
              popup.bindComponent(
                new OrderItemEdit({
                  target: popup.getContentArea(),
                  props: {
                    order: order,
                    item: Object.assign({}, item)
                  }
                })
              );
            }
          },
          buttons: [
            External.Button(M.SAVE, External.Button.CLASS_MIDDLE_BLUE, function (popup, context) {
              let item = popup.component().getSelectedItem();
              updateOrderItem(item);
              popup.close();
            }),
            External.Button.MIDDLE_CANCEL
          ]
        });
      } else if (tag === "delete") {
        External.Tools.prompt(M.DELETE, M.DELETE_PRODUCT_FROM_ORDER, function () {
          deleteOrderItem(item);
        });
      }
    });
  };

  let deleteOrderItem = async function (item) {
    let updateResult = await makeWebsiteRequest({
      method: "post",
      action: "deleteOrderItem",
      data: {
        itemId: item.id,
        orderId: order.id
      }
    });
    order = updateResult.data;
    showingNotificationBox = true;
  };

  let addOrderItem = async function (selectedOption) {
    let updateResult = await makeWebsiteRequest({
      method: "post",
      action: "addOrderItem",
      data: {
        orderId: order.id,
        option: JSON.stringify(selectedOption)
      }
    });
    order = updateResult.data;
    showingNotificationBox = true;
  };

  let updateOrderItem = async function (item) {
    let updateResult = await makeWebsiteRequest({
      method: "post",
      action: "updateOrderItem",
      data: {
        orderId: order.id,
        itemId: item.id,
        quantity: item.quantity,
        price: item.price
      }
    });
    order = updateResult.data;
    showingNotificationBox = true;
  };

  let loadStoreProducts = (callback) => {
    if (products === null) {
      External.Tools.getStoreProducts((loadedProducts) => {
        products = loadedProducts;
        callback(products);
      });
    } else {
      callback(products);
    }
  };

  let addOrderItemBtnClick = () => {
    loadStoreProducts((products) => {
      External.Tools.chooseMultiple({
        title: M.SELECT_PRODUCTS,
        message: "",
        allowNewItems: false,
        single: true,
        items: products,
        selectedItems: [],
        noItemsMessage: M.NOTHING_TO_SELECT,
        width: 440,
        isModal: true,
        getItemId: function (item) {
          return item.id;
        },
        getItemText: function (item) {
          return item.name;
        },
        onSelect: function (products) {
          External.Tools.showWindow({
            noTitle: true,
            width: 520,
            isModal: false,
            withDarkBackground: true,
            forceAtRight: External.showBuilderPopupsOnFullHeight,
            action: {
              onShow: function (popup, ui) {
                popup.bindComponent(
                  new OrderItemAdd({
                    target: popup.getContentArea(),
                    props: {
                      order: order,
                      productId: products[0].id
                    }
                  })
                );
              }
            },
            buttons: [
              External.Button(M.ADD, External.Button.CLASS_MIDDLE_BLUE, function (popup, context) {
                let option = popup.component().getSelectedOption();
                addOrderItem(option);
                popup.close();
              }),
              External.Button.MIDDLE_CANCEL
            ]
          });
        }
      });
    });
  };

  let sendDigitalGoods = async () => {
    let response = makeWebsiteRequest({
      action: "sendDigitalGoodsEmails",
      data: {
        id: order.id
      }
    });
    External.Tools.showAutohideMessage(1000, M.EMAIL_SENT, 400);
  };

  let showMoreActions = (event) => {
    const items = [];
    if (hasDigitalGoods) {
      items.push({ text: M.SEND_DIGITAL_GOODS_EMAIL, icon: "fa-file", tag: "send-digital-goods" });
    }
    items.push({ text: M.ADD_NEW_ITEM, icon: "fa-plus", tag: "add" });
    if (order.status !== "ARCHIVED") {
      items.push({ text: M.ARCHIVE_ORDER, icon: "fa-trash", tag: "archive" });
    }

    items.push({ text: M.SEND_ORDER_DETAILS, icon: "fa-envelope", tag: "notify" });

    if (!options.inPopup) {
      items.push({
        text: M.PRINT,
        icon: "fa fa-print",
        tag: "print"
      });
    }

    External.Tools.showTooltipOptions(External.$(event.target), items, function (target, idx, text, tag) {
      switch (tag) {
        case "print":
          setTimeout(() => {
            window.print();
          }, 200);
          break;
        case "add":
          addOrderItemBtnClick();
          break;
        case "send-digital-goods":
          sendDigitalGoods();
          break;
        case "notify":
          sendOrderDetailsToClient(false);
          break;
        case "archive":
          External.Tools.prompt(M.ARCHIVE_ORDER, M.ARCHIVE_ORDER_DESC, async function () {
            let result = await makeWebsiteRequest({
              action: "archiveOrder",
              data: {
                id: order.id
              }
            });
            order = result.data;
            details.status = order.status;
          });
          break;
      }
    });
  };

  let onPaidBtnClick = (event) => {
    const items = [{ text: details.paid ? M.MARK_NOT_PAID : M.MARK_PAID, icon: "fa-money", tag: "paid" }];

    External.Tools.showTooltipOptions(External.$(event.target), items, function (target, idx, text, tag) {
      details.paid = !details.paid;
      setChanged(changed || details.paid !== order.paid);
    });
  };

  let onStatusBtnClick = (event) => {
    let options = [];

    if (details.status !== "PENDING") {
      options.push({ text: M.MARK_PENDING, icon: "fa-circle-o", tag: "pending" });
    }

    if (hasPhysicalProducts && details.status !== "SHIPPED") {
      options.push({ text: M.MARK_AS_SHIPPED, icon: "fa-truck", tag: "shipped" });
    }

    if (details.status !== "COMPLETED") {
      options.push({ text: M.MARK_AS_COMPLETED, icon: "fa-check", tag: "completed" });
    }

    if (details.status !== "CANCELED") {
      options.push({ text: M.MARK_AS_CANCELED, icon: "fa-ban", tag: "canceled" });
    }

    External.Tools.showTooltipOptions(External.$(event.target), options, function (target, idx, text, tag) {
      details.status = tag.toUpperCase();
      setChanged(true);
    });
  };

  let saveOrderDetails = async () => {
    let markedAsPaid = details.paid && !order.paid;

    let result = await makeWebsiteRequest({
      action: "setOrderDetails",
      method: "post",
      data: {
        id: order.id,
        details: JSON.stringify(details)
      }
    });

    order = result.data;

    External.Tools.showCornerMessage(1500, M.ORDER_UPDATE_SUCCESS, External.Tools.TOOLTIP_TYPE_SUCCESS);
    setChanged(false);
    if (hasDigitalGoods && markedAsPaid) {
      External.Tools.prompt(M.SEND_DIGITAL_GOODS_EMAIL, M.SEND_DIGITAL_GOODS_EMAIL_PROMPT, sendDigitalGoods, ignore, M.YES, M.NO);
    }
    showingNotificationBox = true;
  };

  let setChanged = (isChanged) => {
    changed = isChanged;
    External.CPanel.setChanged(changed);
  };

  let saveNotes = async () => {
    let response = await makeWebsiteRequest({
      action: "setOrderNotes",
      data: {
        id: id,
        notes: JSON.stringify(order.meta.notes || [])
      }
    });
  };

  let addNote = () => {
    let notes = order.meta.notes || [];
    notes = notes.filter((note) => note && note.value && note.value.trim());
    notes.push({
      time: new Date().getTime(),
      value: ""
    });
    order.meta.notes = notes;
    editNote(notes.length - 1);
  };

  let removeNote = (idx) => {
    External.Tools.prompt(M.DELETE_NOTE, M.DEL_CONFIRM, function () {
      order.meta.notes.splice(idx, 1);
      order.meta.notes = order.meta.notes;
      saveNotes();
    });
  };

  let cancelEditNote = (idx) => {
    let note = order.meta.notes[idx];
    delete note.editedValue;
    delete note.edited;
    if (!note.value.trim()) {
      order.meta.notes.splice(idx, 1);
      order.meta.notes = order.meta.notes;
    } else {
      order.meta.notes[idx] = order.meta.notes[idx];
    }
  };

  let saveNote = (idx) => {
    let note = order.meta.notes[idx];
    note.value = note.editedValue || "-";
    delete note.editedValue;
    delete note.edited;
    order.meta.notes[idx] = order.meta.notes[idx];
    saveNotes();
  };

  let editNote = (idx) => {
    let note = order.meta.notes[idx];
    note.edited = true;
    note.editedValue = note.value;
    order.meta.notes[idx] = order.meta.notes[idx];
    canAddNote = false;
  };

  let sendOrderDetailsToClient = async (isUpdateEmail) => {
    let response = await makeWebsiteRequest({
      action: "sendOrderDetailsToClient",
      data: {
        id: id,
        isUpdateEmail: isUpdateEmail
      }
    });
    External.Tools.showAutohideMessage(1000, M.EMAIL_SENT, 400);
    showingNotificationBox = false;
  };

  let getShippingLabel = function () {
    if (!order.shipping) {
      return "No shipping";
    }
    switch (order.shipping.kind) {
      case "speedy":
      case "speedy-address":
      case "speedy-office":
        return M.SEND_WITH_SPEEDY;
      default:
        return M.FULFILL_ORDER;
    }
  };

  let showWaybillDetails = () => {
    switch (order.shipping.kind) {
      case "speedy":
      case "speedy-address":
      case "speedy-office":
        External.WebPlatform.Fulfillment.Speedy().showWaybillDetails(order);
        break;
    }
  };

  let fulfillOrder = () => {
    if (isFulfilled && !hasWaybill) {
      fulfillOrderManually();
      return;
    }

    switch (order.shipping.kind) {
      case "speedy":
      case "speedy-address":
      case "speedy-office":
        if (isFulfilled) {
          return;
        }

				External.WebPlatform.Fulfillment.Speedy().fulfillOrder(order, () => {
					// re-render
					order = order;
					details.status = "SHIPPED";
				});
				break;
			case "fulfillment":
        createFulfillmentOrder();
        break;
      default:
        fulfillOrderManually();
        break;
    }
  };

  let goToContact = () => {
    External.Tools.closeAllPopups(() => {
      External.CPanel.goToUrl("/me/crm/contact/" + order.contactId);
    });
  };

  let fulfillOrderManually = () => {
    External.Tools.showWindow({
      title: M.FULFILL_ITEMS,
      width: 520,
      isModal: false,
      withDarkBackground: true,
      forceAtRight: External.showBuilderPopupsOnFullHeight,
      action: {
        onShow: function (popup, ui) {
          popup.bindComponent(
            new ManualFulfillment({
              target: popup.getContentArea(),
              props: {
                number: order.meta.trackingNo,
                carrier: order.meta.courier,
                trackingUrl: order.meta.trackingUrl
              }
            })
          );
        }
      },
      buttons: [
        External.Button(M.SAVE, External.Button.CLASS_MIDDLE_BLUE, async function (popup, context) {
          let fulfullmentDetails = popup.component().getFulfillmentDetails();
          fulfullmentDetails.orderId = order.id;
          let response = await makeWebsiteRequest({
            action: "setFulfillmentDetails",
            method: "post",
            data: fulfullmentDetails
          });
          if (!response.data) {
            return;
          }
          order = response.data;
          details.status = order.status;
          popup.close();
        }),
        External.Button.MIDDLE_CANCEL
      ]
    });
  };

	let createFulfillmentOrder = async () => {
    let response = await makeWebsiteRequest({
      action: "createFulfillmentOrder",
      method: "post",
      data: {
        orderId: order.id
      }
    });

    order = response.data;
  };

  let formatTime = (datetime) => {
    let dateFormatted = External.Tools.dateToShortString(datetime, true, true);
    let timeFormatted = External.Tools.timeToString(datetime, External.WebsiteSettings.getDateFormat() === "uk");
    return dateFormatted + " " + timeFormatted;
  };

  let setup = async () => {
    setChanged(false);
    showingNotificationBox = false;

    let response = await makeWebsiteRequest({
      action: "getOrderDetails",
      data: {
        id: id
      }
    });

    if (!response.data) {
      return;
    }
    order = response.data;
    let date = External.Tools.localDateTimeStringToDate(order.created);
    createdTime = formatTime(date);

    details = {};
    bulkEditFields.forEach((field) => {
      let value = order;
      let key = "";

      field.split(".").forEach((part) => {
        value = value ? value[part] : "";
        key = part;
      });

      details[key] = value;
    });

    billingDetailsVisible = !order.hasShipping
  };

  let toggleBillingDetails = () => {
    billingDetailsVisible = !billingDetailsVisible;
  }

  $: canAddNote = order && order.meta && order.meta.notes ? order.meta.notes.filter((note) => note.edited === true).length === 0 : true;

  onMount(setup);

  onDestroy(() => {
    AppCache.update((cache) => {
      cache.lastOrder = order;
      return cache;
    });
  });
</script>

{#if order}
	<div class="flex flex-col">
		<div class="account-settings overflow store-products flex-1 horizontal-flex-table with-top-background" in:fade>

			<div class="content-wrapper crm-profile-wrapper full-size flex flex-row-col-responsive">
				<!-- Contact SIdebar Top Part -->
				<div class="crm-contact-sidebar white-bg">
					<div class="col20 contact-sidebar flex flex-col t-center align-center relative white-bg">
						<a class="back-with-arrow absolute" rel="tab" href="/me/store/orders">
							<svg class="rounded-effect" height="48" viewBox="0 -960 960 960" width="48" xmlns="http://www.w3.org/2000/svg">
								<path d="M359-242 120-481l239-239 43 43-166 166h604v60H236l166 166-43 43Z"></path>
							</svg>
						</a>
						<h4 class="col20 f-left mt-5"><b data-role="name">{M.INVOICE} #{order.no}</b></h4>
						<span class="col20 f-left" data-role="email">{createdTime}</span>
						<!-- Status Buttons V1 -->
						<div class="col20 f-left mt-15 mb-10 t-center flex align-center justify-center">
							<div class="f-none inline-block order-status-wrapper v2 mr-5">
								<h5>Pay {M.STATUS}</h5>
								<div class="order-status-select rounded-effect" on:click={(event) => onPaidBtnClick(event)}>
									<p class="status-dot" class:green={details.paid} class:pending={!details.paid}></p>
									<span>{details.paid ? M.PAID : M.NOT_PAID}</span>
								</div>
							</div>
							<div class="f-none inline-block order-status-wrapper v2 mr-5">
								<h5>{M.ORDER} {M.STATUS}</h5>
								<div class="order-status-select rounded-effect " on:click={(event) => onStatusBtnClick(event)}>
									<p class="status-dot {details.status.toLowerCase()}"></p>
									<span>{M["ORDER_STATUS_" + details.status] || details.status}</span>
								</div>
							</div>
							{#if order.status !== "ARCHIVED"}
								<div class="f-none inline-block order-status-wrapper v2"
                   on:click={(event) => showMoreActions(event)}>
                  <div class="contact-cirle-action-icon rounded-effect">
										<svg height="48" viewBox="0 -960 960 960" width="48" xmlns="http://www.w3.org/2000/svg"
                       class="">
											<path
												d="M207.858-432Q188-432 174-446.142q-14-14.141-14-34Q160-500 174.142-514q14.141-14 34-14Q228-528 242-513.858q14 14.141 14 34Q256-460 241.858-446q-14.141 14-34 14Zm272 0Q460-432 446-446.142q-14-14.141-14-34Q432-500 446.142-514q14.141-14 34-14Q500-528 514-513.858q14 14.141 14 34Q528-460 513.858-446q-14.141 14-34 14Zm272 0Q732-432 718-446.142q-14-14.141-14-34Q704-500 718.142-514q14.141-14 34-14Q772-528 786-513.858q14 14.141 14 34Q800-460 785.858-446q-14.141 14-34 14Z"></path>
										</svg>
									</div>
								</div>
							{/if}
						</div>
						<!-- Status Buttons V1 END-->
						<section class="contact-details mt-15">
							<label class="label">
								<h5>{M.NAME}</h5>
								<input
									type="text"
									bind:value={details.customerName}
									on:keyup={() => {
                    setChanged(true);
                  }}/>
							</label>
							<label class="label">
								<h5>{M.EMAIL}</h5>
								<input
									type="email"
									bind:value={details.customerEmail}
									on:keyup={() => {
                    setChanged(true);
                  }} />
                <a on:click={goToContact} class="small-circle-button rounded-effect"><span
                  class="material-icons-outlined"> arrow_forward </span></a>
              </label>
              <label class="label">
                <h5>{M.TOTAL}</h5>
                <input class="readonly-input" type="text" value={External.Tools.currencyToStrWithSymbol(order.total)}
                       readonly style="font-weight: bold" />
              </label>
              <label class="label">
                <h5>{M.PAYMENT}</h5>
                <input class="readonly-input" type="text" readonly value={getPaymentMethod()}/>
							</label>
							<label class="label">
								<h5>{M.TRANSACTION_ID}</h5>
								<input class="readonly-input readonly-input-14" style="font-size: 12px;"type="text" readonly
                       value={order.transactionId} />
              </label>
              {#if order.attributes}
                {#if order.attributes.utm_source}
                  <label class="label">
                    <h5>{M.UTM_SOURCE_LBL}</h5>
                    <input class="readonly-input" type="text" readonly value={order.attributes.utm_source} />
                  </label>
                {/if}
                {#if order.attributes.utm_medium}
                  <label class="label">
                    <h5>{M.UTM_MEDIUM_LBL}</h5>
                    <input class="readonly-input" type="text" readonly value={order.attributes.utm_medium} />
                  </label>
                {/if}
                {#if order.attributes.utm_campaign}
                  <label class="label">
                    <h5>{M.UTM_CAMPAIGN_LBL}</h5>
                    <input class="readonly-input" type="text" readonly value={order.attributes.utm_campaign} />
                  </label>
                {/if}
                {#if order.attributes.utm_term}
                  <label class="label">
                    <h5>{M.UTM_TERM_LBL}</h5>
                    <input class="readonly-input" type="text" readonly value={order.attributes.utm_term} />
                  </label>
                {/if}
                {#if order.attributes.utm_content}
                  <label class="label">
                    <h5>{M.UTM_CONTENT_LBL}</h5>
                    <input class="readonly-input" type="text" readonly value={order.attributes.utm_content} />
                  </label>
                {/if}
              {/if}
              {#if order.additionalFields && order.additionalFields.length}
                {#each order.additionalFields as field}
                  <label class="label">
                    {#if field.name}
                      <h5 style="max-width: 150px;">{field.field || M.FILE}</h5>
                      <a target="_blank" class="bold underline" style="font-size: 14px;"
                         href={field.value}>{field.name}</a>
                    {:else}
                      <h5 style="max-width: 150px;" class="button-tooltip" data-top="true"
                          data-tooltip={field.field || M.ADDITIONAL_INFO}>{field.field || M.ADDITIONAL_INFO}</h5>
                      <div class="flex flex-row flex-1">
                        <span class="none-soft printable">{field.value}</span>
												<input
													class="readonly-input button-tooltip flex-1 non-printable"
													data-left="true"
													data-timeout="6000"
													type="text"
													readonly
													value={field.value}
													data-tooltip={field.value}/>
												<a on:click={() => copy(field.value)}>
                        <span class="material-icons-outlined flex-none button-tooltip non-printable" data-tooltip={M.COPY}
                               style="font-size:20px;">content_copy</span></a>
                      </div>
                    {/if}
                  </label>
                {/each}
              {/if}
              <!-- Status Buttons V1
							<div class="col20 f-left mt-5 mb-10">
								<div class="col9n f-left order-status-wrapper">
									<h5>Pay {M.STATUS}</h5>
									<div class="order-status-select" class:green={details.paid} class:pending={!details.paid} on:click={(event) => onPaidBtnClick(event)}>
										{details.paid ? M.PAID : M.NOT_PAID}
									</div>
								</div>
								<div class="col9n f-right order-status-wrapper">
									<h5>{M.ORDER} {M.STATUS}</h5>
									<div class="order-status-select {details.status.toLowerCase()}" on:click={(event) => onStatusBtnClick(event)}>
										{M["ORDER_STATUS_" + details.status] || details.status}
									</div>
								</div>
							</div>-->
							<!-- Status Buttons V1 END-->
							<!--
							<label class="label">
                <h5>{M.PAID}</h5>
                <div class="order-status-select" class:green={details.paid} class:pending={!details.paid}
                     on:click={(event) => onPaidBtnClick(event)}>
                  {details.paid ? M.PAID : M.NOT_PAID}
                </div>
              </label>
              <label class="label">
                <h5>{M.STATUS}</h5>
                <div class="order-status-select {details.status.toLowerCase()}"
                     on:click={(event) => onStatusBtnClick(event)}>
                  {M["ORDER_STATUS_" + details.status] || details.status}
                </div>
              </label>
              -->
							<label class="label notes-wrapper">
								<h5>{M.NOTES}</h5>
								<div class="col20 f-left t-left">
									{#if order.meta.notes && order.meta.notes.length}
										{#each order.meta.notes as note, idx}
											{#if note.edited}
												<div class="col20 f-left flex flex-row align-center note-wrapper space-between">
													<textarea bind:value={note.editedValue}/>
													<div style="width: 23px; margin-left: 10px;" class="flex-none">
														<a class="mb-10" on:click={() => saveNote(idx)}>
															<span class="material-icons-outlined" style="font-size:20px;">done</span>
														</a>
														<a on:click={() => cancelEditNote(idx)}>
															<span class="material-icons-outlined" style="font-size:20px;">close</span>
														</a>
													</div>
												</div>
											{:else}
												<div class="col20 f-left flex flex-row align-center note-wrapper space-between">
													<span>{note.value}<br/><i class="comment-time">{formatTime(new Date(note.time))}</i></span>
													<div style="width: 23px; margin-left: 10px;" class="flex-none">
														<a on:click={() => editNote(idx)} class="mb-10">
															<span class="material-icons-outlined" style="font-size:20px;">edit</span>
														</a>
														<a on:click={() => removeNote(idx)}>
															<span class="material-icons-outlined" style="font-size:20px;">delete</span>
														</a>
													</div>
												</div>
											{/if}
										{/each}
									{/if}
									{#if canAddNote}
										<a class="small-button blue circle-effect ml-0 inline-block" on:click={addNote}>{M.ADD_NOTE}</a>
									{/if}
								</div>
							</label>
							<div class="col20 f-left none-soft"/>
						</section>
					</div>
					<!-- Contact Sidebar Top Part END -->
					{#if order.hasShipping}
						<div class="col20 contact-sidebar flex flex-col t-center align-center relative white-bg mt-10">
							<section class="contact-details">
								<h4 class="col20 f-left t-left mb-15">{M.SHIPPING_DETAILS}</h4>
								{#if order.shipping?.pickup}
									<div class="flex align-center order-pickup-option col20 f-left">
										<svg class="flex-none mr-10" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#222222"><path d="M160-200h80v-320h480v320h80v-426L480-754 160-626v426Zm-80 80v-560l400-160 400 160v560H640v-320H320v320H80Zm280 0v-80h80v80h-80Zm80-120v-80h80v80h-80Zm80 120v-80h80v80h-80ZM240-520h480-480Z"/></svg>
										{order.shipping.name}: <b style="font-family:sans-serif;margin-left: 5px;">{order.shipping.extra.pickupPlace}</b>
									</div>
								{:else}
								<label class="label">
									<h5>{M.PHONE}</h5>
									<PhoneInput bind:value={details.shippingAddress.phone} onChange={() => {
                    setChanged(true);
                  }}/>
								</label>
								<label class="label">
									<h5>{M.COMPANY_NAME}</h5>
									<input
										type="text"
										bind:value={details.shippingAddress.companyName}
										on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								<label class="label">
									<h5>{M.COMPANY_ID}</h5>
									<input
										type="text"
										bind:value={details.shippingAddress.companyId}
										on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								<label class="label">
									<h5>{M.ADDRESS}</h5>
									<input
										type="text"
										bind:value={details.shippingAddress.address}
										on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								<label class="label">
									<h5 style="visibility: hidden;">{M.ADDRESS}</h5>
									<input
										type="text"
										bind:value={details.shippingAddress.address2}
										on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								{#if order.shipping && order.shipping.kind === 'speedy-office' && order.shipping.extra && order.shipping.extra.speedy &&order.shipping.extra.speedy.officeName}
				  <label class="label">
					<h5>{M.SPEEDY_OFFICE}</h5>
					<input
					  type="text"
					  value={order.shipping.extra.speedy.officeName}
					  readonly
					  disabled
					  />
				  </label>
				{/if}
                <label class="label">
                  <h5>{M.CITY}</h5>
                  <input
                    type="text"
                    bind:value={details.shippingAddress.city}
                    on:keyup={() => {
                      setChanged(true);
                    }} />
                </label>
                <label class="label">
                  <h5>{M.STATE}</h5>
                  <input
                    type="text"
                    bind:value={details.shippingAddress.state}
                    on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								<label class="label">
									<h5>{M.POSTAL_CODE}</h5>
									<input
										type="text"
										bind:value={details.shippingAddress.zipCode}
										on:keyup={() => {
                      setChanged(true);
                    }}/>
								</label>
								<label class="label">
									<h5>{M.COUNTRY}</h5>
									<select
										bind:value={details.shippingAddress.country}
										on:change={() => {
                      setChanged(true);
                    }}>
										{#each External.COUNTRIES as country, idx}
											<option value={External.COUNTRY_CODES[idx]}>{country}</option>
										{/each}
									</select>
								</label>
								{/if}
								<div class="col20 f-left none-soft"/>
							</section>
						</div>
					{/if}
					<div class="col20 contact-sidebar flex flex-col t-center align-center relative white-bg mt-10">
						<section class="contact-details">
							<div class="col20 f-left flex space-between align-center">
								<h4 class="f-left t-left">{M.BILLING_DETAILS}</h4>
								<a class="blue-text flex-none" on:click={toggleBillingDetails}>{billingDetailsVisible ? M.HIDE_DETAILS : M.SHOW_DETAILS}</a>
							</div>
							{#if billingDetailsVisible}
								<div class="col20 f-left" transition:slide>
									<label class="label mt-10">
										<h5>{M.PHONE}</h5>
										<PhoneInput bind:value={details.billingAddress.phone} onChange={() => {
										setChanged(true);
									}}/>
									</label>
									<label class="label">
										<h5>{M.COMPANY_NAME}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.companyName}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.COMPANY_ID}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.companyId}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.ADDRESS}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.address}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5 style="visibility: hidden;">{M.ADDRESS}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.address2}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.CITY}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.city}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.STATE}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.state}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.POSTAL_CODE}</h5>
										<input
											type="text"
											bind:value={details.billingAddress.zipCode}
											on:keyup={() => {
                      setChanged(true);
                    }}/>
									</label>
									<label class="label">
										<h5>{M.COUNTRY}</h5>
										<select
											bind:value={details.billingAddress.country}
											on:change={() => {
                      setChanged(true);
                    }}>
											{#each External.COUNTRIES as country, idx}
												<option value={External.COUNTRY_CODES[idx]}>{country}</option>
											{/each}
										</select>
									</label>
								</div>
							{/if}
							<div class="col20 f-left none-soft"/>
						</section>
					</div>
				</div>

				<div class="padded-crm-wrapper flex flex-col">
					<div class="col20 f-left contact-profile-content" data-role="profile-data" style="border: none;">
						<h3 class="roboto col20 mt-25">{M.PRODUCTS}</h3>
						<div class="productsTable universal-table-wrapper mt-25">
                <div class="order-item">
                  {#if order.items.length}
                    <div class="scrollable-table orders-scrollable-table summary-scrollable-table">
                      <table style="margin-top: 0;">
                        <thead>
                        <tr class="flex flex-row align-center" style="padding-bottom: 0;">
                          <th class="flex-none mw-60">&nbsp;</th>
                          <th class="flex-none mw-72"><a>{M.IMAGE}</a></th>
                          <th class="flex-none"><a>{M.PRODUCT}</a></th>
                          <th class="flex-none"><a>{M.VARIATION}</a></th>
                          <th class="flex-none mw-80"><a>{M.QUANTITY}</a></th>
                          <th class="flex-none"><a>{M.PRICE}</a></th>
                          <th class="flex-none"><a>{M.SKU}</a></th>
                        </tr>
                        </thead>
                        <tbody>
                        {#each order.items as item}
                          <tr class="flex flex-row align-center">
                            <td class="flex-none mw-60">
                              <a on:click={(event) => showProductOptions(event, item)}>
                                <i class="material-icons-outlined">settings</i>
                              </a>
                            </td>
                            <td class="flex-none mw-72"><img src={getImageSrc(item)}
                                                             style="max-width: 65px; border-radius: 6px;" /></td>
                            <td class="flex-none flex align-center">
                              {item.title}
                              {#if item.bump}
                                <span title="Bump" class="material-icons-outlined product-order-type">add_box</span>
                              {:else if item.saleType === "UPSELL"}
                                <span title="Upsell"
                                      class="material-icons-outlined product-order-type">arrow_upward</span>
                              {:else if item.saleType === "DOWNSELL"}
                                <span title="Downsell"
                                      class="material-icons-outlined product-order-type">arrow_downward</span>
                              {/if}
                            </td>
                            <td class="flex-none">
                              {getVariation(item)}
                              {#if item.additions && item.additions.length}
                                {#if item.variation && item.variation.length}
                                  <br /><br />
                                {/if}
                                {#each item.additions as addition, idx}
                                  {#if idx > 0}
                                    <br /><br />
                                  {/if}
                                  <span class="bold">{addition.name}</span>
                                  <br />
                                  {addition.value}
                                {/each}
                              {/if}
                            </td>
                            <td class="flex-none mw-80">{item.quantity}</td>
                            <td class="flex-none"
                                style="font-size: 15px;">{External.Tools.currencyToStrWithSymbol(item.price)}</td>
                            <td class="flex-none">{item.sku || "-"}</td>
                          </tr>
                        {/each}
                        </tbody>
                      </table>
                    </div>
                  {:else}
                    <h4>{M.NO_ITEMS_IN_ORDER}</h4>
                  {/if}
                  <div class="col20 f-left order-total-wrapper mt-15">
                    {#if order.discount}
                      <h4 class="f-left col20 mb-10 ta-right">
                        <span class="light-gray-text f-14-n mr-10 roboto">
                          {M.DISCOUNT_VALUE}
                          {order.discount.name} ({order.discount.code}):
                        </span>
                        {#if order.discountValue}
                          {External.Tools.currencyToStrWithSymbol(-order.discountValue)}
                        {/if}
                      </h4>
                    {/if}

									<h4 class="f-left col20 mb-10 ta-right">
										<span class="light-gray-text f-14-n mr-10 roboto">{M.SUBTOTAL}:</span>
										{External.Tools.currencyToStrWithSymbol(order.subTotal)}
									</h4>

									{#if order.paymentType === 'cod' && order.meta.codFee}
										<h4 class="f-left col20 mb-10 ta-right">
											<span class="light-gray-text f-14-n mr-10 roboto">{M.COD_FEE}:</span>
											{External.Tools.currencyToStrWithSymbol(order.meta.codFee)}
										</h4>
									{/if}

									{#each order.preShippingTaxes as tax}
										<h4 class="f-left col20 mb-10 ta-right">
                      <span class="light-gray-text f-14-n mr-10 roboto">
                        {tax.name}
												{#if tax.type === 0}
                          ({tax.amount}%)
                        {/if}
												{#if External.Website.storePriceWithTax}
                          *{M.INCLUDED}*
                        {/if}
												:
                      </span>
											{External.Tools.currencyToStrWithSymbol(tax.total)}
										</h4>
									{/each}

									{#if order.hasShipping && order.shipping}
										<h4 class="f-left col20 mb-10 ta-right">
                      <span class="light-gray-text f-14-n mr-10 roboto">
                        {M.SHIPPING_COST} - {order.shipping.name}:
                      </span>
											{External.Tools.currencyToStrWithSymbol(order.shipping.cost)}
										</h4>
										{#if order.shipping?.pickup}
											<h4 class="f-left col20 mb-10 ta-right">
												<span class="light-gray-text f-14-n mr-10 roboto">
													{M.PICKUP_FROM_LOCATION.replace('%x%', order.shipping.extra.pickupPlace)}
												</span>
											</h4>
										{/if}
									{/if}

									{#each order.postShippingTaxes as tax}
										<h4 class="f-left col20 mb-10 ta-right">
                      <span class="light-gray-text f-14-n mr-10 roboto">
                        {tax.name}
												{#if tax.type === 0}
                          ({tax.amount}%)
                        {/if}
												{#if External.Website.storePriceWithTax}
                          *{M.INCLUDED}*
                        {/if}
												:
                      </span>
											{External.Tools.currencyToStrWithSymbol(tax.total)}
										</h4>
									{/each}
									<h4 class="f-left col20 ta-right mt-10 total-wrapper">
										<span class="light-gray-text f-12-u mr-10 roboto">{M.TOTAL}:</span>
										{External.Tools.currencyToStrWithSymbol(order.total)}
									</h4>
									{#if order.meta && order.meta.collectedFromCustomer && order.meta.collectedFromCustomer != order.total && order.paymentType !== "bank" && order.paymentType !== "cod"}
										<h4 class="f-left col20 ta-right mt-10">
											<p class="collected-wrapper">
												<span class="material-icons mr-10"> check_circle_outline </span>
												<span class="light-gray-text f-14-n mr-10 roboto">{M.COLLECTED_AMOUNT}:</span>
												{External.Tools.currencyToStrWithSymbol(order.meta.collectedFromCustomer)}
											</p>
										</h4>
									{/if}
                  {#if order.meta && order.meta.ecommerceFee}
                    <p class="f-left col20 ta-right light-gray-text roboto mt-15">
                      {External.format(M.INCLUDED_PLATFORM_FEE, "x", External.Tools.currencyToStrWithSymbol(order.meta.ecommerceFee))}
                    </p>
                  {/if}
								</div>

								{#if showingNotificationBox}
									<div class="col20 f-left gray-section t-center mt-35">
										<h4 class="col20 f-left t-center mt-25">{M.ORDER_CHANGES_MADE_LBL}<br/>{M.ORDER_NOTIFY_MSG}</h4>
										<div class="f-left t-center col20 mt-15 mb-25">
											<a class="middle-button blue f-none inline-block"
                           on:click={() => sendOrderDetailsToClient(true)}>
												{M.SEND_ORDER_UPDATE} <span class="material-icons-outlined"
                                                      style="margin-left:7px; margin-right: 0;"> email </span>
											</a>
										</div>
									</div>
								{/if}
							</div>
						</div>
					</div>

					{#if hasPhysicalProducts && order.shipping}
						<div class="contact-profile-content profile-content mt-25 col20" style="border: none;">
							<h3 class="roboto">{M.ORDER_FULFILLMENT}</h3>
							<div class="col20 f-left mt-15 gray-section t-center">
                {#if isFulfilled}
                  {#if order.meta.courier}
                    <h4 class="no-message-wrapper mb-15 mt-15 t-center fs-24">{M.SENT_VIA}: <b>{order.meta.courier}</b>
                    </h4>
                  {/if}
                  <h4 class="no-message-wrapper mb-15 t-center">
                    {M.TRACKING_NO}:
                    {#if order.meta.trackingUrl}
                      <a
                        href={(order.meta.trackingUrl.toLowerCase().startsWith("http") ? "" : "http://") + order.meta.trackingUrl}
                        target="_blank" class="underline"
                      >{order.meta.trackingNo}</a>
                    {:else}
                      {order.meta.trackingNo}
                    {/if}
                  </h4>
                  {#if hasWaybill}
                    <a on:click={showWaybillDetails}
                       class="middle-button blue f-none inline-block with-icon">{M.VIEW_SHIPPING_DETAILS}</a>
                  {:else}
                    <a on:click={fulfillOrder}
                       class="middle-button blue f-none inline-block with-icon">{M.UPDATE_TRACKING}</a>
                  {/if}

                {:else if isFulfillmentOrderCreated}
                  <h4 class="no-message-wrapper mb-15 mt-15 t-center fs-24">{M.FULFILLMENT_ORDER_CREATED_VIA}
                    <b>{External.WebPlatform.capitalizeFirstLetter(order.extra.fulfillmentOrder.providerType || '')}</b>
                  </h4>
                  <h4 class="no-message-wrapper mb-15 t-center">
                    {M.FULFILLMENT_ORDER_NO}:
                    <a
                      href={order.extra.fulfillmentOrder.externalUrl}
                      target="_blank" class="underline"
                    >{order.extra.fulfillmentOrder.id}</a>
                  </h4>
                {:else}
                  <h4 class="no-message-wrapper mb-15 mt-25 t-center">{M.NOT_FULFILLED}</h4>
                  <div class="f-left t-center col20 mb-25 flex align-center justify-center">
                    <a on:click={fulfillOrder} class="middle-button blue f-none inline-block">{getShippingLabel()}</a>
                    {#if hasThirdPartyFulfillmentApp}
                      <span class="ml-15">or</span> <a class="ml-15 f-16 system-text-color-hover"
                                                       on:click={fulfillOrderManually}><u>{M.FULFILL_MANUALLY}</u></a>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    {#if changed}
      <div class="flex align-center justify-center floating-lane floating-lane-store flex-none"
           style={options.inPopup ? "position: absolute;" : "position: fixed; left: 80px; z-index: 99;"}>
				<a class="middle-button blue flex-none" on:click={saveOrderDetails}> {M.SAVE_ORDER_CHANGES} </a>
				<a on:click={setup} class="text-button f-none inline-block ml-10"> {M.DISCARD_CHANGES_REFRESH} </a>
			</div>
		{/if}
	</div>
{:else if options.inPopup}
	<Loader/>
{/if}

<style>
    @media print {
        :global(.main-navigation, .notification-lane, #action-bar, .back-to-next-step, .crm-option, .scrollable-table td:nth-child(1), .scrollable-table
        td:nth-child(2), .scrollable-table th:nth-child(1), .scrollable-table th:nth-child(2), .contact-profile-sidebar
        > section:nth-child(3), .product-order-type, .small-circle-button, .notes-wrapper, .middle-button, .notes-wrapper, .back-with-arrow, .floating-lane, a.blue-text, .small-circle-button, .contact-cirle-action-icon) {
            display: none !important;
            opacity: 0 !important;
        }

        :global(.working-area-wrapper, .content-wrapper) {
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            transform: none !important;
        }

        :global(.working-area-wrapper-inner) {
            overflow: visible !important;
        }

        :global(.contact-profile-content, .contact-profile-sidebar) {
            width: 100% !important;
            padding-bottom: 20px;
            margin-bottom: 0;
        }

        :global(.contact-profile-sidebar > section) {
            width: 49% !important;
        }

        :global(.contact-profile-sidebar > section:first-child) {
            margin-right: 2%;
        }

        :global(.crm-contact-sidebar) {
			overflow: hidden;
		}

		:global(select) {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            border: none;
            overflow: hidden;
            width: 120%;
        }
    :global(.printable) {
			display: block !important;
		}

		:global(.non-printable) {
			display: none !important;
		}
	}
</style>
